import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Download,
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Users,
  Target,
  Zap,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Award,
  FileImage,
  FileVideo,
  Eye,
  ChevronDown,
  ChevronRight,
  Loader2,
  Settings
} from 'lucide-react';
import { reportService, type ReportData, type TopPerformingCreative } from '../lib/reports';
import { DataExplainer, DATA_EXPLAINERS } from '../components/widgets/DataExplainer';
import { CustomReportBuilder } from '../components/CustomReportBuilder';
import { SecureImage } from '../components/SecureImage';
import GTMService from '../lib/gtm';
import { useAuth } from '../contexts/AuthContext';

export function ReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [expandedSection, setExpandedSection] = useState<string | null>('performance');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const loadReportData = async (period: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportService.getReportData(period);
      setReportData(data);
      
      // Track report generation
      if (user?.id) {
        GTMService.trackCustomEvent('generate_report', {
          report_type: 'analytics',
          date_range: period,
          total_analyses: data.overview.totalAnalyses,
          launch_ready_rate: data.overview.launchReadyRate,
          organization_id: user.organization_id
        }, user.id);
      }
    } catch (err) {
      console.error('Error loading report data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const csvData = await reportService.exportReportData(selectedPeriod, 'csv');
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rulevia-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Track report export
      if (user?.id && reportData) {
        GTMService.trackCustomEvent('export_report', {
          report_type: 'analytics',
          export_format: 'csv',
          date_range: selectedPeriod,
          total_analyses: reportData.overview.totalAnalyses,
          organization_id: user.organization_id
        }, user.id);
      }
    } catch (err) {
      console.error('Error exporting report:', err);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    loadReportData(period);
  };

  const handleCreativeClick = (creative: TopPerformingCreative) => {
    if (creative.analysisId) {
      navigate(`/dashboard/analysis?id=${creative.analysisId}`);
    } else {
      navigate(`/dashboard/analysis?creativeId=${creative.id}`);
    }
  };

  useEffect(() => {
    loadReportData(selectedPeriod);
  }, []);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Failed to load analytics data</p>
            <p className="text-sm text-red-600">{error}</p>
            <button 
              onClick={() => loadReportData(selectedPeriod)}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">No report data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6 sm:space-y-8">
      <Helmet>
        <title>Reports - Rulevia</title>
      </Helmet>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Analytics & Reports</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Comprehensive insights into your creative performance and compliance trends.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          <select 
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
          >
            <option value="all">All Time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <div className="flex gap-2 w-full sm:w-auto">
            <button 
              onClick={() => setShowCustomBuilder(true)}
              className="flex-1 sm:flex-initial bg-white border border-gray-300 text-gray-700 px-4 sm:px-6 py-2 min-h-[44px] rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Custom Report</span>
              <span className="sm:hidden">Custom</span>
            </button>
            <button 
              onClick={handleExportReport}
              className="flex-1 sm:flex-initial bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 sm:px-6 py-2 min-h-[44px] rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export Report</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            {reportData.overview.totalAnalyses > 0 && reportData.overview.totalAnalysesTrend !== 0 ? (
              <div className={`flex items-center text-xs sm:text-sm ${reportData.overview.totalAnalysesTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reportData.overview.totalAnalysesTrend > 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />}
                {reportData.overview.totalAnalysesTrend > 0 ? '+' : ''}{Math.abs(reportData.overview.totalAnalysesTrend)}%
              </div>
            ) : (
              <div className="text-xs text-gray-400">No trend data</div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{reportData.overview.totalAnalyses.toLocaleString()}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Total Analyses</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            {reportData.overview.totalAnalyses > 0 && reportData.overview.launchReadyRateTrend !== 0 ? (
              <div className={`flex items-center text-xs sm:text-sm ${reportData.overview.launchReadyRateTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reportData.overview.launchReadyRateTrend > 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />}
                {reportData.overview.launchReadyRateTrend > 0 ? '+' : ''}{Math.abs(reportData.overview.launchReadyRateTrend)}%
              </div>
            ) : (
              <div className="text-xs text-gray-400">No trend data</div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{reportData.overview.launchReadyRate}%</h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-xs sm:text-sm">Launch Ready Rate</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.launchReady.title}
              explanation={DATA_EXPLAINERS.launchReady.explanation}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            {reportData.overview.totalAnalyses > 0 && reportData.overview.avgScoreTrend !== 0 ? (
              <div className={`flex items-center text-xs sm:text-sm ${reportData.overview.avgScoreTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reportData.overview.avgScoreTrend > 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />}
                {reportData.overview.avgScoreTrend > 0 ? '+' : ''}{Math.abs(reportData.overview.avgScoreTrend)}%
              </div>
            ) : (
              <div className="text-xs text-gray-400">No trend data</div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{reportData.overview.avgScore}%</h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-xs sm:text-sm">Average Score</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.overallScore.title}
              explanation={DATA_EXPLAINERS.overallScore.explanation}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            {reportData.overview.totalAnalyses > 0 && reportData.overview.creditsUsedTrend !== 0 ? (
              <div className={`flex items-center text-xs sm:text-sm ${reportData.overview.creditsUsedTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reportData.overview.creditsUsedTrend > 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />}
                {reportData.overview.creditsUsedTrend > 0 ? '+' : ''}{Math.abs(reportData.overview.creditsUsedTrend)}%
              </div>
            ) : (
              <div className="text-xs text-gray-400">No trend data</div>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{reportData.overview.creditsUsed}</h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-xs sm:text-sm">Credits Used</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.creditsUsed.title}
              explanation={DATA_EXPLAINERS.creditsUsed.explanation}
            />
          </div>
        </motion.div>
      </div>

      {/* Performance Analysis Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-white rounded-xl border border-gray-200"
      >
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <button
            onClick={() => toggleSection('performance')}
            className="flex items-center justify-between w-full text-left min-h-[44px]"
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Performance Analysis</h2>
                <p className="text-gray-600 text-xs sm:text-sm">AI-powered performance insights and predictions</p>
              </div>
            </div>
            <div className="ml-2 flex-shrink-0">
              {expandedSection === 'performance' ? 
                <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                <ChevronRight className="w-5 h-5 text-gray-400" />
              }
            </div>
          </button>
        </div>

        {expandedSection === 'performance' && (
          <div className="p-4 sm:p-6">
            {/* Performance Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-purple-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Avg Performance</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Predicted success rate</p>
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-1">{reportData.performance.avgPerformanceScore}%</div>
                <div className="flex items-center text-green-600 text-xs sm:text-sm">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                  +{reportData.performance.performanceTrend}% vs last month
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                  <div className="flex items-center min-w-0">
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">High Performers</h3>
                      <p className="text-xs sm:text-sm text-gray-600">Score ≥ 90%</p>
                    </div>
                    <DataExplainer 
                      title={DATA_EXPLAINERS.highPerformers.title}
                      explanation={DATA_EXPLAINERS.highPerformers.explanation}
                      className="ml-1 sm:ml-2"
                    />
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">{reportData.performance.highPerformers}</div>
                <div className="text-xs sm:text-sm text-gray-600">
                  of {reportData.performance.totalCreatives} creatives
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                  <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Platform</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Best performance</p>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-green-600 mb-1">{reportData.performance.topPerformingPlatform}</div>
                <div className="text-xs sm:text-sm text-gray-600">
                  Best performing platform
                </div>
              </div>
            </div>

            {/* Top Performing Creatives */}
            <div>
              <div className="flex items-center mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Performing Creatives</h3>
                <DataExplainer 
                  title={DATA_EXPLAINERS.topPerformers.title}
                  explanation={DATA_EXPLAINERS.topPerformers.explanation}
                />
              </div>
              {reportData.topPerformingCreatives.length > 0 ? (
                <div className="space-y-3">
                  {reportData.topPerformingCreatives.map((creative) => (
                    <div 
                      key={creative.id} 
                      onClick={() => handleCreativeClick(creative)}
                      className="border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-all cursor-pointer group"
                    >
                      {/* Mobile Layout */}
                      <div className="sm:hidden p-3 space-y-3">
                        {/* Header with thumbnail and title */}
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0">
                            <SecureImage
                              src={creative.thumbnail}
                              alt={creative.name}
                              className="w-14 h-14 rounded-lg object-cover"
                              fallbackSrc="https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=400"
                            />
                            <div className="absolute -top-1 -right-1">
                              {creative.type === 'video' ? (
                                <FileVideo className="w-4 h-4 text-purple-600" />
                              ) : (
                                <FileImage className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm text-gray-900 group-hover:text-purple-600 transition-colors truncate">
                              {creative.name}
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                              {creative.project} • {new Date(creative.date).toLocaleDateString()}
                            </p>
                            <div className="flex items-center gap-1 mt-2">
                              {creative.platforms.slice(0, 2).map((platform, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                  {platform}
                                </span>
                              ))}
                              {creative.platforms.length > 2 && (
                                <span className="text-xs text-gray-500">+{creative.platforms.length - 2}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Scores */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-4">
                            <div>
                              <div className="text-base font-bold text-purple-600">{creative.performanceScore}%</div>
                              <div className="text-xs text-gray-500">performance</div>
                            </div>
                            <div>
                              <div className="text-base font-bold text-blue-600">{creative.complianceScore}%</div>
                              <div className="text-xs text-gray-500">compliance</div>
                            </div>
                          </div>
                          <Eye className="w-4 h-4 text-purple-600" />
                        </div>
                      </div>
                      
                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-center space-x-4 p-4">
                        <div className="relative">
                          <SecureImage
                            src={creative.thumbnail}
                            alt={creative.name}
                            className="w-16 h-16 rounded-lg object-cover"
                            fallbackSrc="https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=400"
                          />
                          <div className="absolute -top-1 -right-1">
                            {creative.type === 'video' ? (
                              <FileVideo className="w-5 h-5 text-purple-600" />
                            ) : (
                              <FileImage className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">{creative.name}</h4>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            <span>{creative.project}</span>
                            <span>{new Date(creative.date).toLocaleDateString()}</span>
                            <div className="flex items-center space-x-1">
                              {creative.platforms.slice(0, 3).map((platform, idx) => (
                                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  {platform}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="flex items-center space-x-4">
                            <div>
                              <div className="text-lg font-bold text-purple-600">{creative.performanceScore}%</div>
                              <div className="text-xs text-gray-500">performance</div>
                            </div>
                            <div>
                              <div className="text-lg font-bold text-blue-600">{creative.complianceScore}%</div>
                              <div className="text-xs text-gray-500">compliance</div>
                            </div>
                            <div className="p-2 hover:bg-gray-100 rounded-lg transition-colors group-hover:bg-purple-50">
                              <Eye className="w-4 h-4 text-gray-600 group-hover:text-purple-600" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <FileImage className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
                  <p className="text-sm sm:text-base">No creatives analyzed yet</p>
                  <p className="text-xs sm:text-sm">Start analyzing creatives to see performance insights</p>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Key Insights Grid */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        {/* Most Common Issues */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
        >
          <div className="flex items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Most Common Issues</h2>
            <DataExplainer 
              title={DATA_EXPLAINERS.commonIssues.title}
              explanation={DATA_EXPLAINERS.commonIssues.explanation}
            />
          </div>
          
          {reportData.topIssues.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {reportData.topIssues.map((issue, index) => (
                <div key={index} className="flex items-center justify-between p-2 sm:p-3 hover:bg-gray-50 rounded-lg transition-colors min-h-[44px]">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{issue.issue}</div>
                    <div className="text-xs sm:text-sm text-gray-600">{issue.count} occurrences</div>
                  </div>
                  <div className="flex items-center space-x-1 sm:space-x-2 ml-2">
                    {issue.trend === 'up' && <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />}
                    {issue.trend === 'down' && <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
              <p className="text-sm sm:text-base">No compliance issues found</p>
              <p className="text-xs sm:text-sm">Great job maintaining compliance!</p>
            </div>
          )}
        </motion.div>

        {/* Platform Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6"
        >
          <div className="flex items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Platform Performance</h2>
            <DataExplainer 
              title={DATA_EXPLAINERS.platformPerformance.title}
              explanation={DATA_EXPLAINERS.platformPerformance.explanation}
            />
          </div>
          
          {reportData.platformStats.length > 0 ? (
            <div className="space-y-3 sm:space-y-4">
              {reportData.platformStats.map((platform) => (
                <div key={platform.name}>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${platform.color} rounded-full flex-shrink-0`}></div>
                      <span className="font-medium text-gray-900 text-sm sm:text-base truncate">{platform.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 ml-2">
                      {Math.round(platform.avgScore)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(platform.avgScore, 100)}%` }}
                    ></div>
                  </div>
                  <div className="mt-0.5 sm:mt-1 text-xs text-gray-500">
                    {platform.analyses} {platform.analyses === 1 ? 'analysis' : 'analyses'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <Globe className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
              <p className="text-sm sm:text-base">No platform data available</p>
              <p className="text-xs sm:text-sm">Analyze creatives to see platform performance</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Custom Report Builder Modal */}
      <CustomReportBuilder 
        isOpen={showCustomBuilder}
        onClose={() => setShowCustomBuilder(false)}
        currentPeriod={selectedPeriod}
      />

    </div>
  );
}